import { useQuery } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import type {
  SurroundingLogsInitialResponse,
  SurroundingLogsLoadMoreResponse,
  SurroundingLogsParams,
} from '../types/surroundingLogs.types';

const RAW_BASE_URL =
  process.env['NX_PUBLIC_API_BASE_URL'] || 'http://localhost:8080';
const BASE_URL = RAW_BASE_URL.endsWith('/')
  ? RAW_BASE_URL.slice(0, -1)
  : RAW_BASE_URL;

export const useSurroundingLogs = (
  accountId: string | undefined,
  params: {
    log_stream: string;
    timestamp: number;
    count?: number;
    job_ids?: string;
  },
  options?: { enabled?: boolean },
) => {
  return useQuery<SurroundingLogsInitialResponse>({
    queryKey: [
      'surroundingLogs',
      accountId,
      params.log_stream,
      params.timestamp,
      params.count,
      params.job_ids,
    ],

    queryFn: async () => {
      const response = await axios.get(
        `${BASE_URL}/accounts/${accountId}/logs/surrounding`,
        {
          params: {
            log_stream: params.log_stream,
            timestamp: params.timestamp,
            ...(params.count && { count: params.count }),
            ...(params.job_ids && { job_ids: params.job_ids }),
          },
          withCredentials: true,
          timeout: 120000,
        },
      );
      return response.data;
    },

    enabled:
      Boolean(accountId) &&
      Boolean(params.log_stream) &&
      (options?.enabled ?? true),
    staleTime: 0,
    retry: false,
  });
};

export const useLoadMoreSurroundingLogs = (accountId: string | undefined) => {
  return useMutation<
    SurroundingLogsLoadMoreResponse,
    Error,
    SurroundingLogsParams
  >({
    mutationFn: async (params) => {
      const response = await axios.get(
        `${BASE_URL}/accounts/${accountId}/logs/surrounding`,
        {
          params: {
            log_stream: params.log_stream,
            timestamp: params.timestamp,
            ...(params.count && { count: params.count }),
            ...(params.job_ids && { job_ids: params.job_ids }),
            direction: params.direction,
            next_token: params.next_token,
          },
          withCredentials: true,
          timeout: 120000,
        },
      );
      return response.data;
    },
  });
};
